import React, { useEffect, useState, useContext, useRef } from "react"
import { FirebaseContext } from "../Firebase"
import LoadingSpinner from "../loading/LoadingSpinner.js"
import "./CertificatePage.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClock, faCheckCircle, faDownload, faShare, faCopy } from "@fortawesome/free-solid-svg-icons"
import { OverlayTrigger, Popover } from "react-bootstrap"
import CertificateNotFound from "./CertificateNotFound"
import * as htmlToImage from "html-to-image"

const CertificatePage = ({ certUUID }) => {
  const { firebase } = useContext(FirebaseContext)
  // Remove profile state, not needed
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Individual state for each field
  const [certificateId, setCertificateId] = useState("")
  const [userId, setUserId] = useState("")
  const [programId, setProgramId] = useState("")
  const [time, setTime] = useState("")
  const [title, setTitle] = useState("")
  const [completedAt, setCompletedAt] = useState("")
  const [skills, setSkills] = useState([])
  const [description, setDescription] = useState("")
  const [userName, setUserName] = useState("")
  const [showCopied, setShowCopied] = useState(false)
  const [showIdCopied, setShowIdCopied] = useState(false)
  const certificateRef = useRef()

  useEffect(() => {
    if (firebase && certUUID) {
      const fetchData = async () => {
        try {
          const data = await firebase.getCertificateById(certUUID)
          if (!data) {
            setError("Certificate not found")
            return
          }
          setCertificateId(data.id || "")
          setUserId(data.user_id || "")
          setProgramId(data.program_id || "")
          setTitle(data.title || "")
          setCompletedAt(data.completed_at || "")
          setSkills(Array.isArray(data.mastery) ? data.mastery : [])
          setDescription(data.description || "")
          setUserName(data.user_name || "")
          setTime(data.time || "")
          // setLoading(false)
        } catch (e) {
          console.error("Error loading certificate data:", e)
          setError("Failed to load certificate")
          // setLoading(false)
        } finally {
          setLoading(false)
        }
      }
      fetchData()
    }
  }, [firebase, certUUID])

  // Export certificate as image with dynamically measured aspect ratio
  const handleExportImage = async () => {
    if (!certificateRef.current) return
    const el = certificateRef.current
    // Find all elements with hide-on-mobile inside the certificate
    const hiddenEls = el.querySelectorAll('.hide-on-mobile')
    // Remove the class before export
    hiddenEls.forEach(node => node.classList.remove('hide-on-mobile'))

    // Add export class to certificate container for text sizing
    el.classList.add('certificate-exporting-text')

    // Dynamically measure the rendered size
    const rect = el.getBoundingClientRect()
    const aspectWidth = rect.width
    const aspectHeight = rect.height
    // Reduce export size by 30%
    const scale = 0.4
    const exportWidth = Math.round(2480 * scale)
    const exportHeight = Math.round((exportWidth * aspectHeight) / aspectWidth)
    // Store original style
    const originalWidth = el.style.width
    const originalHeight = el.style.height
    const originalTransform = el.style.transform
    // Set to scaled size for export
    el.style.width = exportWidth + "px"
    el.style.height = exportHeight + "px"
    el.style.transform = "scale(1)"
    try {
      const dataUrl = await htmlToImage.toPng(el, {
        width: exportWidth,
        height: exportHeight,
        pixelRatio: 1,
        backgroundColor: "#fff",
        style: {
          width: exportWidth + "px",
          height: exportHeight + "px",
          transform: "scale(1)",
        },
      })
      const link = document.createElement("a")
      link.download = `${userName} ${title} Certificate.png`
      link.href = dataUrl
      link.click()
    } catch (err) {
      alert("Failed to export image")
    } finally {
      // Restore original style
      el.style.width = originalWidth
      el.style.height = originalHeight
      el.style.transform = originalTransform
      // Restore the class after export
      hiddenEls.forEach(node => node.classList.add('hide-on-mobile'))
      el.classList.remove('certificate-exporting-text')
    }
  }

  const handleShare = async () => {
    // Remove any subdomain from the URL (e.g., foo.wingspans.com -> wingspans.com)
    const { protocol, pathname, search, hash, host } = window.location
    // Split host by . and keep only the last two parts (domain + tld)
    const hostParts = host.split(".")
    const rootHost = hostParts.length > 2 ? hostParts.slice(-2).join(".") : host
    const url = `${protocol}//${rootHost}${pathname}${search}${hash}`
    try {
      await navigator.clipboard.writeText(url)
      setShowCopied(true)
      setTimeout(() => setShowCopied(false), 2000) // Hide after 2 seconds
    } catch (err) {
      console.error("Failed to copy: ", err)
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea")
      textArea.value = url
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
      setShowCopied(true)
      setTimeout(() => setShowCopied(false), 2000) // Hide after 2 seconds
    }
  }

  const handleCopyId = async () => {
    const certificateId = "12313654323"
    try {
      await navigator.clipboard.writeText(certificateId)
      setShowIdCopied(true)
      setTimeout(() => setShowIdCopied(false), 2000) // Hide after 2 seconds
    } catch (err) {
      console.error("Failed to copy: ", err)
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea")
      textArea.value = certificateId
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
      setShowIdCopied(true)
      setTimeout(() => setShowIdCopied(false), 2000) // Hide after 2 seconds
    }
  }

  const popover = (
    <Popover id="copy-popover">
      <Popover.Content>Copied!</Popover.Content>
    </Popover>
  )

  const idPopover = (
    <Popover id="copy-id-popover">
      <Popover.Content>ID Copied!</Popover.Content>
    </Popover>
  )

  if (error === "Certificate not found") {
    return (
      <div className="certificate-page">
        <div className="certificate-hero certificate-hero-with-bg">
          <div className="certificate-container inner">
            <CertificateNotFound />
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <>
        <style jsx global>{`
          header,
          footer,
          nav,
          .header,
          .footer,
          .nav,
          .navbar {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          main {
            margin: 0 !important;
            padding: 0 !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
        `}</style>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f9fa",
          }}
        >
          <LoadingSpinner />
        </div>
      </>
    )
  }

  return (
    <>
      <div className="certificate-page">
        <div className="certificate-hero certificate-hero-with-bg">
          <div className="certificate-container inner">
            <div className="certificate-hero-flex">
              <div className="certificate-hero-text">
                <h1>
                  {userName}
                  {userName && userName.slice(-1).toLowerCase() === "s" ? "'" : "'s"}
                </h1>
                <label className="certificate-label">Certificate in</label>
                <h2>{title}</h2>
                <p>
                  {/* {description} */}
                  This Certificate is awarded to {userName} in recognition of their successful completion of the {title} course, including hands-on
                  tasks in{" "}
                  {skills && skills.length > 0
                    ? skills.length === 1
                      ? skills[0]
                      : skills.length === 2
                      ? skills.join(" and ")
                      : skills.slice(0, -1).join(", ") + ", and " + skills[skills.length - 1]
                    : "various areas"}
                  .
                </p>
                <div className="certificate-meta">
                  <div className="certificate-meta-item">
                    <FontAwesomeIcon icon={faClock} className="certificate-meta-icon" aria-hidden="true" />
                    {/* You can add duration if available */}
                    <span className="certificate-meta-text">{time || ""}</span>
                  </div>

                  <div className="certificate-meta-item">
                    <FontAwesomeIcon icon={faCheckCircle} className="certificate-meta-check-icon" aria-hidden="true" />
                    <span className="certificate-meta-date">{completedAt ? new Date(completedAt).toLocaleDateString() : ""}</span>
                  </div>

                  <div className="certificate-meta-item">

                  <OverlayTrigger trigger="manual" placement="top" show={showCopied} overlay={popover}>
                    <button
                      onClick={handleShare}
                      className="certificate-share-btn"
                      style={{
                        background: "none",
                        border: "none",
                        color: "#CAFE13",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "18px",
                        fontWeight: "500",
                        padding: "0 ",
                      }}
                      title="Share certificate"
                    >
                      <FontAwesomeIcon
                        icon={faShare}
                        style={{
                          color: "#CAFE13",
                          fontSize: "22px",
                        }}
                        aria-hidden="true"
                      />
                      <span style={{ color: "white" }}>Share URL</span>
                    </button>
                  </OverlayTrigger>
                  </div>
                </div>
              </div>
              <div className="certificate-hero-certification">
                <div className="certificate-hero-certification-white-box">
                  <div ref={certificateRef} className="certificate-hero-certification-white-box-inner">
                    <img src="/assets/images/certificates/cert-template.png" />
                    <h3 className="certificate-title">Wingspans Course Certificate</h3>
                    <label className="certificate-awarded-to">Awarded To</label>
                    <h1 className="certificate-name">{userName}</h1>
                    <div className="certificate-completion-section">
                      <p className="certificate-completion-text hide-on-mobile">For successfully completing The</p>
                      <h3 className={`certificate-course-title${title.length >= 40 ? " certificate-course-title-small" : ""}`}>{title}</h3>
                      <p className="certificate-authorized-text hide-on-mobile">online course authorized by Wingspans.com</p>
                    </div>
                    <div className="certificate-date-signature hide-on-mobile">{completedAt ? new Date(completedAt).toLocaleDateString() : ""}</div>
                    <div className="certificate-date-label hide-on-mobile">Date</div>
                    <div className="certificate-signature hide-on-mobile">Lindsay Kuhn</div>
                    <div className="certificate-signature-label hide-on-mobile">Lindsay Kuhn, CEO</div>
                  </div>
                  <button
                    className="pdf-export-button button component-button component-button-default component-button-small certificate-download-btn"
                    onClick={handleExportImage}
                  >
                    Download Certificate
                  </button>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
                    <label style={{ textAlign: "center", marginBottom: "0" }}>
                      <span style={{ fontWeight: "500" }}>Certificate ID:</span> {certificateId}
                    </label>
                    <OverlayTrigger trigger="manual" placement="top" show={showIdCopied} overlay={idPopover}>
                      <button
                        onClick={handleCopyId}
                        className="certificate-copy-id-btn"
                        style={{
                          background: "none",
                          border: "none",
                          color: "#58c8db",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "16px",
                          padding: "4px",
                        }}
                        title="Copy Certificate ID"
                      >
                        <FontAwesomeIcon
                          icon={faCopy}
                          style={{
                            color: "#58c8db",
                            fontSize: "16px",
                          }}
                          aria-hidden="true"
                        />
                      </button>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {skills && skills.length > 0 && (
          <div className="certificate-content">
            <div className=" inner">
              <div className="certificate-skills">
                <label className="certificate-skills-label">{userName} Has Mastered</label>
                <div className="certificate-skills-grid">
                  {skills.map((skill, idx) => (
                    <CertificateCard key={idx} skill={skill} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const CertificateCard = ({ skill }) => {
  return (
    <div
      className="certificate-skill-card"
      style={{
        display: "flex",
        alignItems: "center",
        background: "#e3f0f3",
        padding: "1.5rem 2rem",
        borderRadius: "10px",
        fontSize: "23px",
        color: "#363636",
        fontWeight: "600",
        minHeight: "150px",
      }}
    >
      {" "}
      <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#C837D3", marginRight: "1.5rem" }} aria-hidden="true" />
      {skill}
    </div>
  )
}

export default CertificatePage
